import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { FileText, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

interface CsvImportEditorProps {
    onImportStart?: (importJobId: string) => void;
    defaultCountry?: string;
}

export default function CsvImportEditor({ onImportStart, defaultCountry: initialCountry }: CsvImportEditorProps) {
    const { t } = useTranslations();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [defaultCountry, setDefaultCountry] = useState(initialCountry ?? '');
    const [skipAddresses, setSkipAddresses] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (initialCountry && initialCountry !== defaultCountry) {
            setDefaultCountry(initialCountry);
        }
    }, [initialCountry, defaultCountry]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type by extension (more reliable than MIME type)
        const validExtensions = ['.csv', '.txt'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

        // Also check MIME types as secondary validation
        const validTypes = ['text/csv', 'text/plain', 'application/csv', 'application/vnd.ms-excel', 'text/x-csv', 'text/comma-separated-values'];

        // Accept if extension is valid OR MIME type is valid (some browsers don't set MIME type correctly)
        if (!validExtensions.includes(fileExtension) && !validTypes.includes(file.type)) {
            toast.error(t('toasts.settings_csvimporteditor.invalid_file_type', 'Please select a valid CSV file (.csv or .txt)'));
            return;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            toast.error(t('toasts.settings_csvimporteditor.file_too_large', 'File size must be less than 10MB'));
            return;
        }

        setSelectedFile(file);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.error(t('toasts.settings_csvimporteditor.no_file_selected', 'Please select a CSV file to upload'));
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append('csv_file', selectedFile);
            if (defaultCountry) {
                formData.append('default_country', defaultCountry);
            }
            if (skipAddresses) {
                formData.append('skip_addresses', '1');
            }

            const response = await csrfFetch('/settings/system/import-companies-csv', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to upload CSV file');
            }

            toast.success(t('toasts.settings_csvimporteditor.upload_success', 'CSV file uploaded successfully. Import job queued.'));

            // Reset form
            setSelectedFile(null);
            setDefaultCountry(initialCountry ?? '');
            setSkipAddresses(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Notify parent component
            if (data.import_job_id && onImportStart) {
                onImportStart(data.import_job_id);
            }
        } catch (error) {
            console.error('Error uploading CSV:', error);
            toast.error(error instanceof Error ? error.message : 'Failed to upload CSV file');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="space-y-2">
            <Input
                ref={fileInputRef}
                id="csv-file"
                type="file"
                accept=".csv,.txt"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="hidden"
            />
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="h-8 px-3"
                >
                    <Upload className="mr-1.5 h-3.5 w-3.5" />
                    {selectedFile ? 'Change CSV' : 'Select CSV'}
                </Button>

                <div className="text-muted-foreground flex min-w-0 flex-1 items-center gap-1 text-xs">
                    {selectedFile ? (
                        <>
                            <FileText className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{selectedFile.name}</span>
                            <span className="shrink-0">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                            <Button type="button" variant="ghost" size="sm" onClick={handleRemoveFile} disabled={isUploading} className="h-6 w-6 p-0">
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        </>
                    ) : (
                        <span className="truncate">CSV or TXT, max 10MB</span>
                    )}
                </div>

                <div className="flex items-center gap-1.5">
                    <Label htmlFor="default-country" className="text-xs">
                        Country
                    </Label>
                    <Input
                        id="default-country"
                        type="text"
                        placeholder="LT"
                        value={defaultCountry}
                        onChange={(e) => setDefaultCountry(e.target.value.toUpperCase())}
                        disabled={isUploading}
                        maxLength={2}
                        className="h-8 w-14 text-xs uppercase"
                    />
                </div>

                <div className="flex items-center gap-1.5">
                    <input
                        type="checkbox"
                        id="skip-addresses"
                        checked={skipAddresses}
                        onChange={(e) => setSkipAddresses(e.target.checked)}
                        disabled={isUploading}
                        className="border-border h-3.5 w-3.5 rounded"
                    />
                    <Label htmlFor="skip-addresses" className="cursor-pointer text-xs font-normal whitespace-nowrap">
                        Skip enrichment
                    </Label>
                </div>

                <Button onClick={handleUpload} disabled={!selectedFile || isUploading} size="sm" className="h-8 px-3">
                    {isUploading ? 'Uploading...' : 'Import'}
                </Button>
            </div>
        </div>
    );
}
